import { useState } from "react"

import type { AgentLinkedIssue } from "@productlane/lib/src/agent-recommendations"
import type { AgentRecommendation } from "@productlane/zero/schema"
import { LinearIcon } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useAuthData } from "@/lib/zero"
import { ENGINEERING_COPY } from "./format"

const GUARD_CODES = new Set([
	"BAD_REQUEST",
	"CONFLICT",
	"NOT_FOUND",
	"PRECONDITION_FAILED",
])

export const openExternal = (url: string) =>
	window.open(url, "_blank", "noopener,noreferrer")

// The row leaves the open list once filed, so the issue the server returns is
// kept here for the pane that stays open.
export function useEngineeringIssue(recommendation: AgentRecommendation) {
	const authData = useAuthData()
	const mutation =
		api.agentSetupActions.createIssueFromRecommendation.useMutation()
	const [created, setCreated] = useState<AgentLinkedIssue | null>(null)
	const issue = created ?? recommendation.payload.linearIssue ?? null

	const create = async ({
		teamId,
		title,
		assigneeId,
	}: {
		teamId: string
		title: string
		assigneeId?: string
	}): Promise<boolean> => {
		if (mutation.isLoading) return false
		try {
			const result = await mutation.mutateAsync({
				recommendationId: recommendation.id,
				teamId,
				title,
				assigneeId,
			})
			setCreated(result)
			toast.success({
				title: ENGINEERING_COPY.createdToast(result.identifier),
				icon: LinearIcon,
				action: {
					label: ENGINEERING_COPY.openInLinear,
					callback: () => openExternal(result.url),
				},
			})
			return true
		} catch (error) {
			const code = (error as { data?: { code?: string } }).data?.code
			// The server's guards (no Linear, a hidden team, already filed) say what to do.
			if (code && GUARD_CODES.has(code) && error instanceof Error) {
				toast.info({ title: error.message, icon: LinearIcon })
				return false
			}
			captureError(error, {
				domain: "linear",
				action: "create issue from agent recommendation",
				workspaceId: authData.workspaceId,
				userId: authData.userId,
				extra: { recommendationId: recommendation.id },
			})
			toast.error({
				title: ENGINEERING_COPY.createFailedToast,
				icon: LinearIcon,
			})
			return false
		}
	}

	return { issue, create, busy: mutation.isLoading }
}
